import React, { useMemo, useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
} from "@mui/material";

const API_BASE_URL = process.env.REACT_APP_SUPERADMIN_APP_API_BASE_URL;

const RichTextEditor = ({
  value,
  onChange,
  placeholder = "Enter text...",
  currentGuideId = null,
}) => {
  const [editorValue, setEditorValue] = useState("");
  const [guideLinkDialogOpen, setGuideLinkDialogOpen] = useState(false);
  const [guidesList, setGuidesList] = useState([]);
  const [loadingGuides, setLoadingGuides] = useState(false);
  const [selectedGuideId, setSelectedGuideId] = useState("");
  const [linkText, setLinkText] = useState("");
  const quillRef = useRef(null);

  // Clean and set initial value
  useEffect(() => {
    if (value !== undefined) {
      try {
        // Clean malformed HTML before setting
        let cleanedValue = value || "";

        // Fix common HTML issues
        if (cleanedValue) {
          // Fix unclosed tags - use loops to handle multiple instances
          let prevValue = "";
          while (cleanedValue !== prevValue) {
            prevValue = cleanedValue;
            cleanedValue = cleanedValue
              .replace(/<b>([^<]*)<b>/gi, "<b>$1</b>")
              .replace(/<strong>([^<]*)<strong>/gi, "<strong>$1</strong>")
              .replace(/<li>([^<]*?)<li>/gi, "<li>$1</li>");
          }

          // Fix unclosed ul/ol tags
          cleanedValue = cleanedValue.replace(
            /<ul>([\s\S]*?)<ul>/gi,
            "<ul>$1</ul>"
          );
          cleanedValue = cleanedValue.replace(
            /<ol>([\s\S]*?)<ol>/gi,
            "<ol>$1</ol>"
          );

          // Use browser parser to fix remaining issues
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = cleanedValue;
          cleanedValue = tempDiv.innerHTML;
        }

        setEditorValue(cleanedValue);
      } catch (error) {
        console.error("Error parsing HTML:", error);
        setEditorValue("");
      }
    } else {
      setEditorValue("");
    }
  }, [value]);

  // Fetch guides list for guide link dialog
  const fetchGuidesList = async () => {
    setLoadingGuides(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/user-guides/list`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.success) {
        // Filter out current guide if editing
        const filteredGuides = currentGuideId
          ? data.data.filter((guide) => guide._id !== currentGuideId)
          : data.data;
        setGuidesList(filteredGuides);
      }
    } catch (error) {
      console.error("Error fetching guides list:", error);
    } finally {
      setLoadingGuides(false);
    }
  };

  // Handle guide link button click
  const handleGuideLinkClick = () => {
    fetchGuidesList();
    setGuideLinkDialogOpen(true);
    // Get selected text from editor
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const selection = quill.getSelection(true);
      if (selection && selection.length > 0) {
        const selectedText = quill.getText(selection.index, selection.length);
        setLinkText(selectedText);
      }
    }
  };

  /**
   * Insert guide link into editor using @ mention format
   * Format: @[Guide Name](guideId)
   * This is simple text format that will be converted to clickable links in recruitment panel
   */
  const handleInsertGuideLink = () => {
    if (!selectedGuideId) return;

    const selectedGuide = guidesList.find((g) => g._id === selectedGuideId);
    if (!selectedGuide) return;

    // Get link text (user entered or use guide name)
    const finalLinkText = linkText.trim() || selectedGuide.navigation_name;

    // Create @ mention format: @[Guide Name](guideId)
    // This format is simple, readable, and easy to parse
    const mentionText = `@[${finalLinkText}](${selectedGuideId})`;

    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const selection = quill.getSelection(true);
      const index = selection ? selection.index : quill.getLength();
      const length = selection ? selection.length : 0;

      // Delete selected text if any
      if (length > 0) {
        quill.deleteText(index, length);
      }

      // Insert @ mention text at cursor position
      // Quill will handle this as plain text, no HTML issues
      quill.insertText(index, mentionText);

      // Move cursor after inserted text
      const newIndex = index + mentionText.length;
      quill.setSelection(newIndex, 0);

      // Quill's onChange will be triggered automatically by insertText
      // No need to manually call handleChange here
    }

    // Close dialog and reset
    setGuideLinkDialogOpen(false);
    setSelectedGuideId("");
    setLinkText("");
  };

  const handleCloseGuideLinkDialog = () => {
    setGuideLinkDialogOpen(false);
    setSelectedGuideId("");
    setLinkText("");
  };

  const modules = useMemo(() => {
    const toolbar = [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image"],
      ["clean"],
    ];

    return {
      toolbar: {
        container: toolbar,
        handlers: {
          "guide-link": handleGuideLinkClick,
        },
      },
      clipboard: {
        matchVisual: false,
      },
    };
  }, [currentGuideId]);

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
  ];

  /**
   * Handle editor content change
   * Simply pass the content to parent component
   * The @ mention format will be preserved as plain text in HTML
   */
  const handleChange = (content) => {
    setEditorValue(content);

    // Console log the HTML content
    console.log("Quill Editor HTML:", content);

    // Pass content to parent component
    // The @[Guide Name](guideId) format will be saved as text in HTML
    // Recruitment panel will parse and convert it to clickable links
    if (onChange) {
      onChange(content);
    }
  };

  // Add custom guide link button to toolbar
  useEffect(() => {
    if (!quillRef.current) return;

    // Wait for Quill to fully initialize
    const timer = setTimeout(() => {
      try {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        const toolbar = quill.getModule("toolbar");
        if (!toolbar || !toolbar.container) return;

        const container = toolbar.container;

        // Check if button already exists
        if (container.querySelector(".ql-guide-link")) {
          return;
        }

        // Create custom button
        const guideLinkButton = document.createElement("button");
        guideLinkButton.type = "button";
        guideLinkButton.className = "ql-guide-link";
        guideLinkButton.innerHTML = `
          <svg viewBox="0 0 18 18" style="width: 18px; height: 18px;">
            <path d="M3 9c0-1.1.9-2 2-2h4a1 1 0 0 1 0 2H5a1 1 0 0 0 0 2h4a1 1 0 0 1 0 2H5c-1.1 0-2-.9-2-2zm8 0a1 1 0 0 1 1-1h4c1.1 0 2 .9 2 2s-.9 2-2 2h-4a1 1 0 0 1-1-1zm1-4h4a1 1 0 0 1 0 2h-4a1 1 0 0 1 0-2zm0 8h4a1 1 0 0 1 0 2h-4a1 1 0 0 1 0-2z" fill="currentColor"/>
          </svg>
        `;
        guideLinkButton.title = "Insert Guide Link";
        guideLinkButton.onclick = (e) => {
          e.preventDefault();
          handleGuideLinkClick();
        };

        // Find a safe insertion point - look for clean button or any existing button
        const cleanButton = container.querySelector(".ql-clean");
        const linkButton = container.querySelector(".ql-link");

        // Try to insert before clean button if it exists and is a child
        if (cleanButton && cleanButton.parentNode === container) {
          container.insertBefore(guideLinkButton, cleanButton);
        }
        // Otherwise try after link button
        else if (linkButton && linkButton.parentNode === container) {
          if (linkButton.nextSibling) {
            container.insertBefore(guideLinkButton, linkButton.nextSibling);
          } else {
            container.appendChild(guideLinkButton);
          }
        }
        // Last resort: append to container
        else {
          container.appendChild(guideLinkButton);
        }
      } catch (error) {
        console.error("Error adding guide link button:", error);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      // Cleanup: remove button if it exists
      if (quillRef.current) {
        try {
          const quill = quillRef.current.getEditor();
          const toolbar = quill?.getModule("toolbar");
          const container = toolbar?.container;
          if (container) {
            const btn = container.querySelector(".ql-guide-link");
            if (btn && btn.parentNode === container) {
              container.removeChild(btn);
            }
          }
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    };
  }, []); // Run only once on mount

  return (
    <Box
      sx={{
        "& .quill": {
          fontFamily: "Outfit",
        },
        "& .ql-container": {
          fontFamily: "Outfit",
          fontSize: "14px",
          minHeight: "200px",
          borderBottomLeftRadius: "8px",
          borderBottomRightRadius: "8px",
        },
        "& .ql-editor": {
          minHeight: "200px",
          fontFamily: "Outfit",
          fontSize: "14px",
          "&.ql-blank::before": {
            fontStyle: "normal",
            color: "#A3AED0",
            fontFamily: "Outfit",
          },
          "& p": {
            margin: "4px 0",
          },
          // CRITICAL FIX: Reset list-style to ensure Quill controls numbering/bullets
          "& ul, & ol": {
            margin: "8px 0",
            paddingLeft: "1.5em",
            listStyle: "none", // Remove default list style
          },
          "& li": {
            marginBottom: "4px",
            paddingLeft: "0.5em",
            position: "relative",
          },
          // Let Quill handle the bullets/numbers through its counter system
          "& li::before": {
            display: "none", // Hide any custom bullets
          },
          "& a": {
            color: "#4BC7E1",
            textDecoration: "underline",
          },
          "& strong, & b": {
            fontWeight: 600,
            color: "#212121",
          },
          "& em, & i": {
            fontStyle: "italic",
            color: "#212121",
          },
          "& *": {
            color: "inherit",
          },
        },
        "& .ql-toolbar": {
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
          border: "1px solid #e5e7eb",
          borderBottom: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
          "& .ql-stroke": {
            stroke: "#6b7280",
          },
          "& .ql-fill": {
            fill: "#6b7280",
          },
          "& .ql-picker-label": {
            color: "#6b7280",
          },
          "& button:hover, & button.ql-active": {
            "& .ql-stroke": {
              stroke: "#9048FB",
            },
            "& .ql-fill": {
              fill: "#9048FB",
            },
          },
        },
        "& .ql-container": {
          border: "1px solid #e5e7eb",
          borderTop: "none",
        },
        "& .ql-guide-link": {
          width: "24px",
          height: "24px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          "&:hover": {
            color: "#9048FB",
          },
          "& svg": {
            width: "18px",
            height: "18px",
          },
        },
      }}
    >
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={editorValue}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />

      {/* Guide Link Dialog */}
      <Dialog
        open={guideLinkDialogOpen}
        onClose={handleCloseGuideLinkDialog}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "12px",
            fontFamily: "Outfit",
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
          Insert Guide Link
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ fontFamily: "Outfit" }}>
                Select Guide
              </InputLabel>
              <Select
                value={selectedGuideId}
                onChange={(e) => setSelectedGuideId(e.target.value)}
                label="Select Guide"
                sx={{ fontFamily: "Outfit" }}
                disabled={loadingGuides}
              >
                {guidesList.length === 0 && !loadingGuides ? (
                  <MenuItem disabled sx={{ fontFamily: "Outfit" }}>
                    No guides available
                  </MenuItem>
                ) : (
                  guidesList.map((guide) => (
                    <MenuItem
                      key={guide._id}
                      value={guide._id}
                      sx={{ fontFamily: "Outfit" }}
                    >
                      {guide.navigation_name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {loadingGuides && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
            </FormControl>
            <TextField
              label="Link Text (optional)"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              placeholder="Enter link text or leave empty to use guide name"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontFamily: "Outfit",
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "Outfit",
                },
              }}
            />
            {selectedGuideId && (
              <Typography
                sx={{
                  fontFamily: "Outfit",
                  fontSize: "12px",
                  color: "#6b7280",
                  fontStyle: "italic",
                }}
              >
                Selected:{" "}
                {
                  guidesList.find((g) => g._id === selectedGuideId)
                    ?.navigation_name
                }
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseGuideLinkDialog}
            sx={{
              fontFamily: "Outfit",
              textTransform: "none",
              color: "#6b7280",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleInsertGuideLink}
            variant="contained"
            disabled={!selectedGuideId}
            sx={{
              fontFamily: "Outfit",
              textTransform: "none",
              backgroundColor: "#9048FB",
              "&:hover": {
                backgroundColor: "#7c3aed",
              },
            }}
          >
            Insert Link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RichTextEditor;
